import React, {useState, useEffect} from "react"
import {useLocation} from "react-router-dom"
import {useLanguage} from "../../context/LanguageContext"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CircularProgress from "@mui/material/CircularProgress"
import "./TableCards.css"
import {Config} from "../../Config/config"
import {sortProductsByOrder} from "../../utils/sortByOrder"

export default function TableCards() {
  const {language} = useLanguage()
  const location = useLocation()
  const {subCategoryId, hasOption} = location.state || {}

  const [products, setProducts] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [loading, setLoading] = useState(true)

  const languageCode = language || "TR"

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)

      const response = await fetch(
        `${Config.ApiBaseUrl}/product/?firm=2&is_active=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${Config.Token}`,
          },
        }
      )
      const data = await response.json()

      // Filter the products by the subCategoryId passed in the route
      const filteredProducts = data.filter(
        (product) => product.sub_category === subCategoryId
      )
      setProducts(sortProductsByOrder(filteredProducts))
      setLoading(false)
    }
    fetchData()
  }, [subCategoryId])

  // Open the modal when a product is clicked
  const openModal = (product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedProduct(null)
  }

  // Get the product's language-specific data
  const getProductLanguageData = (product, languageCode) => {
    const languageData = product.productlanguage.find(
      (lang) => lang.language__code === languageCode
    )
    return (
      languageData ||
      product.productlanguage.find((lang) => lang.language__code === "TR")
    )
  }

  // Get the product price for a specific unit
  const getProductPrice = (product, unitName) => {
    const price = product.productprice.find(
      (price) => price.unit__name === unitName
    )
    return price ? price.price : "Not available"
  }

  // Group products by their unit name
  const groupedByUnit = products.reduce((acc, product) => {
    product.productprice.forEach((price) => {
      const unitName = price.unit__name || "No Unit"
      if (!acc[unitName]) {
        acc[unitName] = []
      }
      acc[unitName].push(product)
    })
    return acc
  }, {})

  return (
    <>
      {loading ? (
        <div className="loading-spinner">
          <CircularProgress size={50} color="black" />
        </div>
      ) : (
        <div
          className={`table-card-grid ${!hasOption ? "has-option-false" : ""}`}
        >
          {hasOption
            ? Object.keys(groupedByUnit).map((unitName) => {
                return (
                  <Accordion key={unitName}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel-${unitName}-content`}
                      id={`panel-${unitName}-header`}
                    >
                      <Typography>{unitName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {groupedByUnit[unitName].map((product) => {
                        const productData = getProductLanguageData(
                          product,
                          languageCode
                        )
                        const productPrice = getProductPrice(product, unitName)

                        return (
                          <div
                            className="card"
                            key={product.id}
                            onClick={() => openModal(product)}
                          >
                            <img
                              className="card-image"
                              src={product.image}
                              alt={product.name}
                            />
                            <div className="card-content">
                              <div className="card-info">
                                <h3 className="card-name">
                                  {productData?.name}
                                </h3>
                                <div className="card-price">
                                  {productPrice} TL
                                </div>
                              </div>
                              <p className="card-description">
                                {productData?.description}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion>
                )
              })
            : products.map((product) => {
                const productData = getProductLanguageData(
                  product,
                  languageCode
                )
                const productPrice = getProductPrice(
                  product,
                  product.productprice[0]?.unit__name
                )

                return (
                  <div
                    className="card"
                    key={product.id}
                    onClick={() => openModal(product)}
                  >
                    <img
                      className="card-image"
                      src={product.image}
                      alt={product.name}
                    />
                    <div className="card-content">
                      <div className="card-info">
                        <h3 className="card-name">{productData?.name}</h3>
                        <div className="card-price">{productPrice} TL</div>
                      </div>
                      <p className="card-description">
                        {productData?.description}
                      </p>
                    </div>
                  </div>
                )
              })}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && selectedProduct && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={closeModal}>
              &times;
            </span>
            <img
              className="modal-image"
              src={selectedProduct.image}
              alt={selectedProduct.name}
            />
            <div className="modal-info">
              {(() => {
                const productData = getProductLanguageData(
                  selectedProduct,
                  languageCode
                )
                return (
                  <>
                    <h3>{productData?.name}</h3>
                    <p>{productData?.description}</p>
                    <div className="modal-price">
                      {getProductPrice(
                        selectedProduct,
                        selectedProduct.productprice[0]?.unit__name
                      )}
                      TL
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
